import { type ReactNode } from 'react'
import { isNotEmpty } from '@terros/common'
import { Divider, Group, Input, Stack, TextInput } from '@mantine/core'
import { useTranslation } from '@front/model'
import { TButton, TCard } from '@components'
import { getReviewRoutingState } from '../reviewRouting'
import { ToggleRow } from './ToggleRow'
import { GoogleBusinessProfileField } from './GoogleBusinessProfileField'
import { type CompanyCardFormProps } from './CompanyCardFields'

export const ReviewFields = ({ form, placeLabel }: CompanyCardFormProps & { placeLabel?: string }): ReactNode => {
  const { t } = useTranslation()
  const { showReviewSettings, showFeedbackInbox, showFeedbackFields } = getReviewRoutingState(form.values)

  function updateTeamInboxEmail(email: string): void {
    form.setFieldValue('teamInboxEmail', isNotEmpty(email) ? email : undefined)
  }

  function updateTeamInboxEnabled(enabled: boolean): void {
    form.setFieldValue('feedbackInboxEnabled', enabled)
    form.validateField('teamInboxEmail')
    form.validateField('starThreshold')
  }

  function updateReviewCtaEnabled(enabled: boolean): void {
    form.setFieldValue('sections.cta', enabled)
    form.validateField('placeId')
    form.validateField('teamInboxEmail')
    form.validateField('starThreshold')
  }

  return (
    <TCard>
      <Stack gap='lg'>
        <TCard.Header>
          <TCard.Title>{t('Review routing')}</TCard.Title>
        </TCard.Header>
        <ToggleRow
          checked={showReviewSettings}
          description={t('A prompt that starts the review flow.')}
          label={t('Leave a review CTA')}
          onChange={updateReviewCtaEnabled}
        />
        {showReviewSettings && (
          <Stack gap='lg'>
            <GoogleBusinessProfileField form={form} placeLabel={placeLabel} />
            {showFeedbackInbox && (
              <>
                <Divider />
                <ToggleRow
                  checked={form.values.feedbackInboxEnabled}
                  description={t('Send negative feedback to a shared company inbox.')}
                  label={t('Feedback Inbox')}
                  onChange={updateTeamInboxEnabled}
                />
              </>
            )}
            {showFeedbackFields && (
              <Stack gap='lg'>
                <TextInput
                  required
                  label={t('Feedback inbox email')}
                  type='email'
                  {...form.getInputProps('teamInboxEmail')}
                  value={form.values.teamInboxEmail ?? ''}
                  onChange={(event) => updateTeamInboxEmail(event.currentTarget.value)}
                />
                <Input.Wrapper
                  description={t('Ratings below this level will be directed to internal feedback.')}
                  error={form.errors.starThreshold}
                  label={t('Positive review threshold')}
                  labelElement='div'
                >
                  <Group gap='sm' mt='xs'>
                    {[
                      { label: t('3 and up'), value: 3 },
                      { label: t('4 and up'), value: 4 },
                      { label: t('5 only'), value: 5 },
                    ].map((option) => {
                      const isSelected = (form.values.starThreshold ?? 4) === option.value
                      return (
                        <TButton
                          key={option.value}
                          aria-pressed={isSelected}
                          icon='star'
                          rounded
                          size='sm'
                          variant={isSelected ? 'primary' : 'secondary'}
                          onClick={() => form.setFieldValue('starThreshold', option.value)}
                        >
                          {option.label}
                        </TButton>
                      )
                    })}
                  </Group>
                </Input.Wrapper>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </TCard>
  )
}
